/**
 * 4.0 通告信息中的传感器类型与 LOD（抬起高度）。
 *
 * sensorType:
 *   0 = 3950
 *   1 = 3955
 *   2 = 3395
 *
 * sensorFlag Bit0-4 为当前 LOD。
 * 前三档（0.7 / 1 / 2 mm）用序号 0 / 1 / 2 下发，设备也可能回报 7 / 10 / 20；
 * 3955 后续档位按数字本身下发（7 / 9 / 12 / 14 / 16）。
 */

export const SENSOR_TYPE_3950 = 0;
export const SENSOR_TYPE_3955 = 1;
export const SENSOR_TYPE_3395 = 2;

export const SENSOR_TYPE_NAMES = {
  [SENSOR_TYPE_3950]: '3950',
  [SENSOR_TYPE_3955]: '3955',
  [SENSOR_TYPE_3395]: '3395',
};

export const SENSOR_FLAG_LOD_MASK = 0x1f;

const LOD_3950 = [
  { value: 0, aliases: [0, 7], label: '0.7 mm', desc: '最低抬起', mm: 0.7 },
  { value: 1, aliases: [1, 10], label: '1 mm', desc: '推荐', mm: 1 },
  { value: 2, aliases: [2, 20], label: '2 mm', desc: '最高抬起', mm: 2 },
];

const LOD_3395 = [
  { value: 1, aliases: [1, 10], label: '1 mm', desc: '推荐', mm: 1 },
  { value: 2, aliases: [2, 20], label: '2 mm', desc: '最高抬起', mm: 2 },
];

const LOD_3955 = [
  { value: 7, aliases: [7], label: '0.7 mm', desc: '最低抬起', mm: 0.7 },
  { value: 9, aliases: [9], label: '0.9 mm', desc: '偏低', mm: 0.9 },
  { value: 12, aliases: [12], label: '1.2 mm', desc: '推荐', mm: 1.2 },
  { value: 14, aliases: [14], label: '1.4 mm', desc: '偏高', mm: 1.4 },
  { value: 16, aliases: [16], label: '1.6 mm', desc: '最高抬起', mm: 1.6 },
];

export const getSensorTypeName = (sensorType) =>
  SENSOR_TYPE_NAMES[Number(sensorType)] || '未知';

export const getLodOptions = (sensorType) => {
  switch (Number(sensorType)) {
    case SENSOR_TYPE_3955:
      return LOD_3955;
    case SENSOR_TYPE_3395:
      return LOD_3395;
    default:
      return LOD_3950;
  }
};

export const extractLodFromSensorFlag = (sensorFlag) => {
  if (sensorFlag === undefined || sensorFlag === null) return null;
  return Number(sensorFlag) & SENSOR_FLAG_LOD_MASK;
};

export const resolveLodOption = (sensorType, rawValue) => {
  if (rawValue === undefined || rawValue === null) return null;
  const value = Number(rawValue);
  return getLodOptions(sensorType).find((opt) => opt.aliases.includes(value)) || null;
};

export const isLodOptionActive = (option, rawValue) =>
  option?.aliases?.includes(Number(rawValue)) === true;

export const normalizeLodWriteValue = (sensorType, rawValue) => {
  const option = resolveLodOption(sensorType, rawValue);
  if (option) return option.value;
  return getLodOptions(sensorType)[0].value;
};

export const getLodMillimeters = (sensorType, rawValue) => {
  const option = resolveLodOption(sensorType, rawValue);
  if (option) return option.mm;
  const value = Number(rawValue);
  if (Number.isFinite(value) && value >= 7) return value / 10;
  if (value === 0) return 0.7;
  if (value === 2) return 2;
  return 1;
};

export const formatLodDisplay = (sensorType, rawValue) => {
  const option = resolveLodOption(sensorType, rawValue);
  if (option) return option.label;
  if (rawValue === undefined || rawValue === null) return '1 mm';
  const mm = getLodMillimeters(sensorType, rawValue);
  return `${mm} mm`;
};

/** 把目标毫米数编成该传感器应下发的 LOD 值，无精确匹配时取最近档。 */
export const encodeLodForMillimeters = (sensorType, mm) => {
  const options = getLodOptions(sensorType);
  const target = Number(mm);
  let best = options[0];
  let bestDiff = Math.abs(best.mm - target);
  for (const option of options) {
    const diff = Math.abs(option.mm - target);
    if (diff < bestDiff) {
      best = option;
      bestDiff = diff;
    }
  }
  return best.value;
};

/** 旧的三档序号 0/1/2（0.7/1/2 mm）按当前传感器编码。 */
export const encodeLegacyLodIndex = (sensorType, index) => {
  const mm = Number(index) === 0 ? 0.7 : Number(index) === 2 ? 2 : 1;
  return encodeLodForMillimeters(sensorType, mm);
};
